import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, BookOpen, Search, MessageSquare, FileText, ScrollText,
  ArrowLeft, BookMarked, Scissors, HelpCircle,
} from "lucide-react";

import SermonOutlinePanel from "../components/preach-kit/SermonOutlinePanel";
import ScriptureSlidesPanel from "../components/preach-kit/ScriptureSlidesPanel";
import BibleSearchPanel from "../components/preach-kit/BibleSearchPanel";
import CompanionPanel from "../components/preach-kit/CompanionPanel";
import NotesToSermonPanel from "../components/preach-kit/NotesToSermonPanel";
import VideoClipperTab from "../components/preach-kit/VideoClipperTab";
import SermonLibraryPanel from "../components/preach-kit/SermonLibraryPanel";
import PreachKitOnboarding from "../components/preach-kit/PreachKitOnboarding";

export default function PreachKit() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("outline");
  const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);
  const [tourOpen, setTourOpen] = useState(false);
  const triggerLibraryRefresh = () => setLibraryRefreshKey((k) => k + 1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-amber-950/20 text-white">
      <PreachKitOnboarding forceOpen={tourOpen} />
      <header className="sticky top-0 z-30 border-b border-amber-500/20 bg-slate-950/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t("back") || "Back"}
            </Button>
            <div className="flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-amber-400" />
              <h1 className="text-lg font-bold tracking-tight" data-testid="preach-kit-title">
                {t("preach_kit") || "PreachKit"}
              </h1>
              <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]">BETA</Badge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="sm"
              onClick={() => { setTourOpen(false); setTimeout(() => setTourOpen(true), 0); }}
              className="text-amber-300 hover:text-amber-200"
              data-testid="open-tour-btn"
              title="Replay the welcome tour"
            >
              <HelpCircle className="w-4 h-4" />
            </Button>
            <div className="text-sm text-slate-400 hidden sm:block">
              {user?.name || user?.email}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="mb-6 -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto sm:overflow-visible scrollbar-thin">
              <TabsList className="inline-flex sm:grid sm:grid-cols-7 w-max sm:w-full bg-slate-900/60 border border-slate-800 gap-0.5">
                <TabsTrigger value="outline" data-testid="tab-outline" className="gap-1 whitespace-nowrap">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t("sermon_outline") || "Outline"}</span>
                  <span className="sm:hidden text-xs">{t("sermon_outline") || "Outline"}</span>
                </TabsTrigger>
                <TabsTrigger value="slides" data-testid="tab-slides" className="gap-1 whitespace-nowrap">
                  <BookOpen className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t("scripture_to_slides") || "Slides"}</span>
                  <span className="sm:hidden text-xs">{t("slides") || "Slides"}</span>
                </TabsTrigger>
                <TabsTrigger value="search" data-testid="tab-search" className="gap-1 whitespace-nowrap">
                  <Search className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t("bible_search") || "Search"}</span>
                  <span className="sm:hidden text-xs">{t("search") || "Search"}</span>
                </TabsTrigger>
                <TabsTrigger value="companion" data-testid="tab-companion" className="gap-1 whitespace-nowrap">
                  <MessageSquare className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t("companion") || "Companion"}</span>
                  <span className="sm:hidden text-xs">{t("companion") || "Companion"}</span>
                </TabsTrigger>
                <TabsTrigger value="notes" data-testid="tab-notes" className="gap-1 whitespace-nowrap">
                  <FileText className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t("notes_to_sermon") || "Notes"}</span>
                  <span className="sm:hidden text-xs">{t("notes") || "Notes"}</span>
                </TabsTrigger>
                <TabsTrigger value="video" data-testid="tab-video" className="gap-1 whitespace-nowrap">
                  <Scissors className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t("video_clipper") || "Video Clipper"}</span>
                  <span className="sm:hidden text-xs">{t("video") || "Video"}</span>
                  <Badge variant="outline" className="ml-0.5 border-amber-500/40 text-amber-300 text-[8px] px-1 py-0 hidden sm:inline-flex">NEW</Badge>
                </TabsTrigger>
                <TabsTrigger value="library" data-testid="tab-library" className="gap-1 whitespace-nowrap">
                  <BookMarked className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t("my_sermons") || "Library"}</span>
                  <span className="sm:hidden text-xs">{t("library") || "Library"}</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="outline"><SermonOutlinePanel t={t} onSaved={triggerLibraryRefresh} /></TabsContent>
            <TabsContent value="slides"><ScriptureSlidesPanel t={t} onSaved={triggerLibraryRefresh} /></TabsContent>
            <TabsContent value="search"><BibleSearchPanel t={t} /></TabsContent>
            <TabsContent value="companion"><CompanionPanel t={t} /></TabsContent>
            <TabsContent value="notes"><NotesToSermonPanel t={t} onSaved={triggerLibraryRefresh} /></TabsContent>
            <TabsContent value="video"><VideoClipperTab t={t} /></TabsContent>
            <TabsContent value="library"><SermonLibraryPanel t={t} refreshKey={libraryRefreshKey} active={activeTab === "library"} /></TabsContent>
          </Tabs>
        </motion.div>
      </main>
    </div>
  );
}
